import { useConfigStore } from '@renderer/stores/configState'
import { useFeatureFlagsStore } from '@renderer/stores/featureFlagsStore'

/**
 * Audible countdown cue.
 *
 * Asked for by an operator whose guests could not tell when the shutter would fire.
 * They had switched the booth to physical-shutter capture purely to use the shutter's
 * NOISE as feedback, and were then worried about the resulting camera wear — so the fix
 * is to give the booth a voice of its own and let the shutter go back to being a shutter.
 *
 * Two cues, and the split matters:
 *   - a click on every second the guest actually SEES, so it tracks the number on screen
 *   - one distinct tone at the moment the photo is really taken
 *
 * The second one deliberately does NOT fire when the countdown reaches zero. With
 * physical-shutter capture the photo can land noticeably later than zero (libgphoto2
 * blocks on autofocus inside the capture call), and a cue at zero would be lying about
 * the very thing the operator says guests get wrong. It rides `onRawCaptured` instead,
 * which fires when the frame is genuinely in hand.
 *
 * Tones are SYNTHESISED with the Web Audio API rather than shipped as files. The booth
 * has never contained an audio asset, and a lazily-decoded mp3 would be late exactly
 * once — on the first shot of the day, in front of a customer. An oscillator has no
 * load, no decode and no first-play penalty. When custom sounds arrive later they can
 * use the existing asset pipeline; this stays as the always-available default.
 *
 * Nothing here ever throws. A booth with no speakers, no output device, or a browser
 * that refuses an AudioContext must take photos exactly as it does today.
 */

/** Registered in the Studio flag registry (config/featureFlags.ts), default OFF. */
export const COUNTDOWN_SOUND_FLAG = 'countdown_sound'

export type CountdownSoundMode = 'off' | 'beep'

const SOUND_MODES: readonly CountdownSoundMode[] = ['off', 'beep']

/** Project config is remote data; anything unrecognised must land on silence. */
export function normaliseCountdownSound(value: unknown): CountdownSoundMode {
  return SOUND_MODES.includes(value as CountdownSoundMode)
    ? (value as CountdownSoundMode)
    : 'off'
}

/**
 * Resolved mode for right now: the project's choice, but only while the flag is on.
 * Gated in both places on purpose — the flag is the fleet kill switch, the project
 * setting is the operator's choice, and either one off means silence.
 */
export function resolveCountdownSound(): CountdownSoundMode {
  if (!useFeatureFlagsStore.getState().getFlag(COUNTDOWN_SOUND_FLAG, false)) return 'off'
  const settings = useConfigStore.getState().config?.pages?.capture?.settings as
    | { countdownSound?: unknown }
    | undefined
  return normaliseCountdownSound(settings?.countdownSound)
}

type WindowWithWebkitAudio = Window & { webkitAudioContext?: typeof AudioContext }

let audioContext: AudioContext | null = null
/** One failed construction is enough; never retry it on every tick of every session. */
let audioUnavailable = false

function getAudioContext(): AudioContext | null {
  if (audioUnavailable) return null
  if (audioContext) return audioContext
  try {
    const Ctor =
      window.AudioContext ?? (window as WindowWithWebkitAudio).webkitAudioContext ?? null
    if (!Ctor) {
      audioUnavailable = true
      return null
    }
    audioContext = new Ctor()
    return audioContext
  } catch {
    // No output device, or the context was refused. Stay silent for the rest of the run.
    audioUnavailable = true
    return null
  }
}

/**
 * One short tone. Shaped with an envelope rather than switched on and off — a bare
 * gain change produces an audible click at both ends, which on a booth speaker sounds
 * like a fault rather than a cue.
 */
function playTone(frequency: number, durationMs: number, peakGain: number): void {
  const ctx = getAudioContext()
  if (!ctx) return
  try {
    // Chromium starts a context suspended until something resembling a gesture. The
    // booth runs unattended, so ask every time; it is a no-op when already running.
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined)

    const now = ctx.currentTime
    const duration = durationMs / 1000
    const oscillator = ctx.createOscillator()
    const gain = ctx.createGain()

    oscillator.type = 'sine'
    oscillator.frequency.setValueAtTime(frequency, now)

    gain.gain.setValueAtTime(0.0001, now)
    gain.gain.exponentialRampToValueAtTime(peakGain, now + 0.012)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration)

    oscillator.connect(gain)
    gain.connect(ctx.destination)
    oscillator.start(now)
    oscillator.stop(now + duration + 0.02)
    oscillator.onended = (): void => {
      // Nodes are single-use; leaving them connected leaks one pair per tick, and a
      // booth runs for a whole shift.
      oscillator.disconnect()
      gain.disconnect()
    }
  } catch {
    // A cue is never worth failing a session over.
  }
}

/** A click for one visible second of the countdown. Short, dry, low-key. */
export function playCountdownTick(): void {
  if (resolveCountdownSound() !== 'beep') return
  playTone(880, 90, 0.18)
}

/**
 * Shortest gap between two capture cues. A dual-camera booth captures both bodies for
 * ONE photo, so the cue would otherwise double up; two real photos are always a whole
 * countdown apart, so nothing legitimate is ever swallowed by this.
 */
const MIN_CAPTURE_CUE_GAP_MS = 300

let lastCaptureCueAt = 0

/**
 * The photo is taken. Higher and longer than a tick so it is unmistakably the end of
 * the sequence and not another count.
 */
export function playCaptureCue(): void {
  if (resolveCountdownSound() !== 'beep') return
  const now = Date.now()
  if (now - lastCaptureCueAt < MIN_CAPTURE_CUE_GAP_MS) return
  lastCaptureCueAt = now
  playTone(1320, 180, 0.22)
}

/** Test-only: forget the cached context/unavailability. */
export function __resetCountdownSoundForTest(): void {
  audioContext = null
  audioUnavailable = false
  lastCaptureCueAt = 0
}
